/**
 * `OriginalOrderPanel` — the server-resolved ORIGINAL order a refund
 * targets (ADR-0099 follow-up). Shared by BOTH the HITL escalation review
 * detail (`EscalationDetail`, pre-decision) and the settled refund audit
 * record (`MandateDetail`, post-decision), so the two surfaces render the
 * original order identically.
 *
 * Mirrors the `SettlementPanel` visual density: thin 1px border, mono
 * uppercase kicker, right-aligned amounts, total separated by a top
 * border. The buyer affordance reuses the SAME `BuyerDetailPanel` reveal
 * the audit detail uses — pointed at `original_record_id`, so the decrypt
 * is the existing access-logged audit path (no new surface): the refund
 * record itself carries no buyer, so this is the only place the buyer can
 * be revealed.
 *
 * `original === null` means the server could not resolve the original on
 * this site (shouldn't happen post-admission-gates) — render the
 * could-not-resolve fallback instead of an empty panel.
 */

import type { AuditRecordId, UserRole } from '@sill/types';

import type { EscalationOriginalOrder } from '../../api/escalations.js';
import { Pill } from '../../components/atoms/index.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { formatIsoDate } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { BuyerDetailPanel } from './BuyerDetailPanel.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/**
 * Build a Shopify Admin deep link to the order from its GID + the store
 * domain. `gid://shopify/Order/7324251521087` + `shop.myshopify.com` →
 * `https://shop.myshopify.com/admin/orders/7324251521087` (the Admin order
 * page shows the human #1001 number). Returns null when either input is
 * missing, the domain is not a bare `*.myshopify.com` host, or the GID's
 * trailing id is not a bare numeric token (defensive — never emit a
 * malformed/hostile href; the caller falls back to plain text).
 *
 * The domain shape is ALSO enforced server-side by the
 * `shopify_order_state.shop_domain` DB CHECK, but we re-validate the
 * anchored `*.myshopify.com` regex here so the safe-href guarantee never
 * rests on a single control (a future projector/CHECK regression cannot
 * smuggle a `javascript:`/open-redirect host into the link).
 */
function shopifyOrderAdminUrl(
  shop_domain: string | undefined,
  shopify_order_id: string | undefined,
): string | null {
  if (shop_domain === undefined || shopify_order_id === undefined) return null;
  if (!/^[a-z0-9][a-z0-9-]{0,60}\.myshopify\.com$/.test(shop_domain)) return null;
  const id = shopify_order_id.split('/').pop();
  if (id === undefined || !/^\d+$/.test(id)) return null;
  return `https://${shop_domain}/admin/orders/${id}`;
}

/**
 * Build a Stripe dashboard deep link to a charge. `ch_…` charges open under
 * `/payments/<id>`; `live` uses the bare host, `test` the `/test/` prefix.
 * Returns null (no link, bare id rendered instead) unless the charge id
 * matches the safe `ch_…` token shape — the safe-href guarantee never
 * depends on un-validated server data.
 */
function stripeChargeUrl(
  stripe_charge_id: string | undefined,
  stripe_mode: 'test' | 'live',
): string | null {
  if (stripe_charge_id === undefined) return null;
  if (!/^ch_[A-Za-z0-9]+$/.test(stripe_charge_id)) return null;
  const prefix = stripe_mode === 'test' ? 'test/' : '';
  return `https://dashboard.stripe.com/${prefix}payments/${stripe_charge_id}`;
}

export function OriginalOrderPanel({
  C,
  original,
  role,
  unresolvedHint = 'Original order could not be resolved on this site.',
}: {
  C: ReturnType<typeof useC>;
  original: EscalationOriginalOrder | null;
  role?: UserRole;
  /**
   * Copy shown in the could-not-resolve fallback. The PRE-decision review
   * queue passes an actionable "refuse this refund" hint; the POST-
   * decision audit record (the refund already executed) keeps the neutral
   * default.
   */
  unresolvedHint?: string;
}): JSX.Element {
  const timeZone = useDisplayTimeZone();
  if (original === null) {
    return (
      <div
        className="p-3 mb-5"
        style={{
          border: `1px dashed ${C.borderSoft}`,
          borderRadius: 2,
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 6,
          }}
        >
          ORIGINAL ORDER
        </div>
        {unresolvedHint}
      </div>
    );
  }

  const order = original.shopify_order;
  const charge = original.stripe_charge ?? null;
  const intent = original.intent_summary;
  const currency = order?.currency ?? charge?.currency;
  const money = (minor: number): string => {
    // Minor units (cents) → major. A malformed currency code throws
    // RangeError — fall back to USD formatting rather than crashing the
    // panel on a bad row (same posture as the settlement panel).
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency !== undefined && currency.length > 0 ? currency : 'USD',
      }).format(minor / 100);
    } catch {
      return `$${(minor / 100).toFixed(2)}`;
    }
  };

  const moneyRow = (label: string, value: string): JSX.Element => (
    <div className="flex items-center justify-between" key={label}>
      <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5 }}>
        {label}
      </span>
      <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>{value}</span>
    </div>
  );

  // Line items: prefer the settled per-line evidence (product names),
  // fall back to the order-level product_title, then the originally
  // approved intent lines (bare SKUs).
  const evidence = order?.line_items_evidence;

  return (
    <div
      className="p-3 mb-5 space-y-2"
      style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2 }}
    >
      <div className="flex items-center justify-between">
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
          ORIGINAL ORDER
        </span>
        <Pill tone="mint">{original.decision.toUpperCase()}</Pill>
      </div>

      {/* Shopify order — the merchant's frame of reference. Deep-link to
          Admin when we have the store domain + a numeric order id (the
          Admin page shows the human #1001 number). Falls back to the bare
          GID when the domain is absent (older records). */}
      {order?.shopify_order_id !== undefined ? (
        (() => {
          const href = shopifyOrderAdminUrl(order.shop_domain, order.shopify_order_id);
          // Task #14 — prefer Shopify's human-readable order name (e.g.
          // "#1001") as the link text; fall back to the generic label
          // when the name is absent (older records).
          const label = order.order_name ?? 'View order in Shopify';
          return href !== null ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: monoFont, fontSize: 11, color: C.limeFg, textDecoration: 'underline', wordBreak: 'break-all' }}
            >
              {label} ↗
            </a>
          ) : (
            <div style={{ fontFamily: monoFont, fontSize: 10, color: C.text, wordBreak: 'break-all' }}>
              {order.shopify_order_id}
            </div>
          );
        })()
      ) : null}

      {/* Stripe charge — the merchant's frame of reference for a Stripe-
          settled original. Deep-link to the Stripe dashboard (mode-aware)
          when the charge id matches the safe `ch_…` shape; falls back to the
          bare id otherwise. */}
      {charge?.stripe_charge_id !== undefined ? (
        (() => {
          const href = stripeChargeUrl(charge.stripe_charge_id, charge.stripe_mode);
          return href !== null ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: monoFont, fontSize: 11, color: C.limeFg, textDecoration: 'underline', wordBreak: 'break-all' }}
            >
              View charge in Stripe ↗
            </a>
          ) : (
            <div style={{ fontFamily: monoFont, fontSize: 10, color: C.text, wordBreak: 'break-all' }}>
              {charge.stripe_charge_id}
            </div>
          );
        })()
      ) : null}

      {/* Sill's own reference for the original order (the signed mandate) —
          labeled so it isn't mistaken for a Shopify order number. */}
      <div
        style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, wordBreak: 'break-all' }}
      >
        Sill mandate · {original.original_mandate_id}
      </div>

      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
        decided {formatIsoDate(original.evaluated_at, timeZone)}
        {order?.financial_status_at_create !== undefined
          ? ` · ${order.financial_status_at_create} at create`
          : ''}
      </div>

      {evidence !== undefined && evidence.length > 0 ? (
        <div className="space-y-1">
          {evidence.map((li, i) => (
            <div key={`${li.sku ?? 'line'}-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, wordBreak: 'break-word' }}>
                <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                  {li.quantity ?? 1} ×
                </span>{' '}
                {li.product_title ?? li.sku ?? 'unresolved line'}
              </div>
              {li.sku !== undefined ? (
                <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, wordBreak: 'break-all' }}>
                  {li.sku}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : order?.product_title !== undefined ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, wordBreak: 'break-word' }}>
          {order.product_title}
        </div>
      ) : intent?.line_items !== undefined && intent.line_items.length > 0 ? (
        <div className="space-y-1">
          {intent.line_items.map((li, i) => (
            <div
              key={`${li.sku}-${i}`}
              style={{ fontFamily: monoFont, fontSize: 11, color: C.text, wordBreak: 'break-all' }}
            >
              <span style={{ color: C.muted }}>{li.quantity} ×</span> {li.sku}
            </div>
          ))}
        </div>
      ) : intent?.sku !== undefined ? (
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.text, wordBreak: 'break-all' }}>
          {intent.sku}
        </div>
      ) : null}

      {order !== null ? (
        <>
          {order.subtotal_minor !== undefined ? moneyRow('SUBTOTAL', money(order.subtotal_minor)) : null}
          {order.tax_minor !== undefined ? moneyRow('TAX', money(order.tax_minor)) : null}
          {order.shipping_minor !== undefined && order.shipping_minor > 0
            ? moneyRow('SHIPPING', money(order.shipping_minor))
            : null}
          {order.total_minor !== undefined ? (
            <div
              className="flex items-center justify-between pt-2"
              style={{ borderTop: `1px solid ${C.borderSoft}` }}
            >
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.text,
                  letterSpacing: 1.5,
                  fontWeight: 600,
                }}
              >
                SETTLED TOTAL
              </span>
              <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 600, color: C.text }}>
                {money(order.total_minor)}
              </span>
            </div>
          ) : null}
        </>
      ) : charge !== null ? (
        <>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
            stripe charge · {charge.state}
          </div>
          {charge.total_minor !== undefined ? (
            <div
              className="flex items-center justify-between pt-2"
              style={{ borderTop: `1px solid ${C.borderSoft}` }}
            >
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.text,
                  letterSpacing: 1.5,
                  fontWeight: 600,
                }}
              >
                SETTLED TOTAL
              </span>
              <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 600, color: C.text }}>
                {money(charge.total_minor)}
              </span>
            </div>
          ) : null}
        </>
      ) : (
        <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          No settlement evidence on record for the original order.
        </div>
      )}

      {/* Buyer reveal — the EXISTING audit buyer-detail flow (access-
          logged decrypt) keyed by the ORIGINAL audit record (the refund
          record carries no buyer). No purge affordance from this surface. */}
      <BuyerDetailPanel
        record_id={original.original_record_id as AuditRecordId}
        {...(role !== undefined ? { role_override: role } : {})}
        hide_purge
      />
    </div>
  );
}
